/**
 * AnalysisOptionsForm Component
 *
 * Collapsible advanced analysis options panel.
 * All shadcn components replaced with Tailwind equivalents.
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useState } from 'react';
import { ChevronDown, Settings } from 'lucide-react';

const ANALYSIS_TYPES = [
  { value: 'general', label: 'General Movement', description: 'Overall feedback on any movement or exercise' },
  { value: 'weightlifting', label: 'Weightlifting Form', description: 'Detailed analysis for squats, deadlifts, etc.' },
  { value: 'martial-arts', label: 'Martial Arts Technique', description: 'Critique for strikes, blocks, and forms' },
  { value: 'injury-prevention', label: 'Injury Prevention', description: 'Safety-focused assessment of movement patterns' },
];

export const AnalysisOptionsForm = ({ options, onOptionsChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const updateOption = (key, value) => {
    onOptionsChange({ ...options, [key]: value });
  };

  return (
    <div className="bg-[#0b1118] border border-white/10 rounded-xl p-4">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-zinc-200 hover:text-orange-400 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4" />
          <span className="font-medium text-sm">Advanced Analysis Options</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Collapsible Content */}
      {isOpen && (
        <div className="mt-4 space-y-4">
          {/* Analysis Type Selection */}
          <div className="space-y-2">
            <label htmlFor="analysisType" className="text-sm text-zinc-300">Analysis Focus</label>
            <select
              id="analysisType"
              value={options.analysisType || 'general'}
              onChange={(e) => updateOption('analysisType', e.target.value)}
              className="w-full bg-[#040608] border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400/50"
            >
              {ANALYSIS_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} -- {type.description}
                </option>
              ))}
            </select>
          </div>

          {/* Frame Rate */}
          <div className="space-y-2">
            <label htmlFor="frameRate" className="text-sm text-zinc-300">Custom Frame Rate (FPS)</label>
            <input
              id="frameRate"
              type="number"
              min="0.1"
              max="30"
              step="0.1"
              placeholder="Default: 1 FPS"
              value={options.customFrameRate || ''}
              onChange={(e) => updateOption('customFrameRate', e.target.value ? Number.parseFloat(e.target.value) : undefined)}
              className="w-full bg-[#040608] border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400/50"
            />
            <p className="text-xs text-zinc-500">Higher FPS for fast movements, lower for static analysis</p>
          </div>

          {/* Video Clipping */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="startOffset" className="text-sm text-zinc-300">Start Time (MM:SS)</label>
              <input
                id="startOffset"
                placeholder="0:00"
                value={options.startOffset || ''}
                onChange={(e) => updateOption('startOffset', e.target.value)}
                className="w-full bg-[#040608] border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400/50"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="endOffset" className="text-sm text-zinc-300">End Time (MM:SS)</label>
              <input
                id="endOffset"
                placeholder="Full video"
                value={options.endOffset || ''}
                onChange={(e) => updateOption('endOffset', e.target.value)}
                className="w-full bg-[#040608] border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400/50"
              />
            </div>
          </div>

          {/* Timestamp References */}
          <div className="flex items-center gap-2">
            <input
              id="useTimestamps"
              type="checkbox"
              checked={options.useTimestamps || false}
              onChange={(e) => updateOption('useTimestamps', e.target.checked)}
              className="w-4 h-4 rounded bg-[#040608] border-white/10 accent-orange-400"
            />
            <label htmlFor="useTimestamps" className="text-sm text-zinc-300">
              Include timestamp references in analysis
            </label>
          </div>

          {/* Custom System Prompt */}
          <div className="space-y-2">
            <label htmlFor="systemPrompt" className="text-sm text-zinc-300">Custom Analysis Prompt (Optional)</label>
            <textarea
              id="systemPrompt"
              placeholder="Override the default analysis approach with your custom instructions..."
              value={options.customSystemPrompt || ''}
              onChange={(e) => updateOption('customSystemPrompt', e.target.value)}
              className="w-full h-20 bg-[#040608] border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-400/50"
            />
            <p className="text-xs text-zinc-500">Leave blank to use the selected analysis type's default prompt</p>
          </div>
        </div>
      )}
    </div>
  );
};
